import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Grid,
  Stack,
  TextField,
  Typography,
  Notification,
  Tooltip,
} from '@xbotvn/mui';
import {
  keyBy,
  uniq,
  values,
} from 'lodash';
import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { listingApi, onlineBookApi } from '../../api';
import Waiting from '../../components/Waiting';
import { getThumbnail, isValidUrl } from '../../libs/utils';

const getCoverFallback = (book) => book?.coverLink || '/defaultBook.jpg';

export default function OnlineBooks() {
  const [searchText, setSearchText] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [sharedBooks, setSharedBooks] = useState([]);
  const [publicBooks, setPublicBooks] = useState([]);
  const [unitBooksLocal, setUnitBooksLocal] = useState([]);
  const [loadingShared, setLoadingShared] = useState(false);
  const [loadingPublic, setLoadingPublic] = useState(false);
  const [loadingUnitBooks, setLoadingUnitBooks] = useState(false);

  const { unitId } = useParams();

  const {
    onlineBooks,
    handling,
    userInfo,
    activeUnit,
    unitInfo,
  } = useSelector(({
    onlineBook,
    user,
    unit,
  }) => ({
    onlineBooks: onlineBook.data,
    handling: onlineBook.handling,
    userInfo: user,
    activeUnit: user.activeUnit,
    unitInfo: unit.data?.[user?.activeUnit],
    units: unit.data,
  }));

  const isLoggedIn = !!userInfo?._id;
  const hasParent = !!unitInfo?.parentId;
  const hasUnit = !!activeUnit;

  useEffect(() => {
    let isMounted = true;
    const cleanup = () => {
      isMounted = false;
    };
    if (!isLoggedIn || !activeUnit || !hasParent) {
      setSharedBooks([]);
      return cleanup;
    }
    const fetchShared = async () => {
      setLoadingShared(true);
      try {
        const { data } = await onlineBookApi.getShared({
          data: {
            unitId: activeUnit,
          },
        });
        if (isMounted) {
          setSharedBooks(data?.records || []);
        }
      } catch (error) {
        if (isMounted) {
          Notification.error('Thông báo', 'Lấy dữ liệu thất bại');
        }
      } finally {
        if (isMounted) {
          setLoadingShared(false);
        }
      }
    };
    fetchShared();
    return cleanup;
  }, [isLoggedIn, activeUnit, hasParent]);

  useEffect(() => {
    let isMounted = true;
    const cleanup = () => {
      isMounted = false;
    };
    if (!isLoggedIn || !hasUnit) {
      setUnitBooksLocal([]);
      return cleanup;
    }
    const fetchUnitBooks = async () => {
      setLoadingUnitBooks(true);
      try {
        const { data } = await listingApi.get({
          collection: 'onlineBook',
          payload: {
            data: {
              unitId: activeUnit,
            },
          },
        });
        if (isMounted) {
          setUnitBooksLocal(data?.records || []);
        }
      } catch (error) {
        if (isMounted) {
          Notification.error('Thông báo', 'Lấy dữ liệu thất bại');
        }
      } finally {
        if (isMounted) {
          setLoadingUnitBooks(false);
        }
      }
    };
    fetchUnitBooks();
    return cleanup;
  }, [isLoggedIn, hasUnit, activeUnit]);

  useEffect(() => {
    let isMounted = true;
    const cleanup = () => {
      isMounted = false;
    };
    if (isLoggedIn || !unitId) {
      setPublicBooks([]);
      return cleanup;
    }
    const fetchPublic = async () => {
      setLoadingPublic(true);
      try {
        const payload = {
          isPublic: true,
        };
        if (unitId) {
          payload.unitId = unitId;
        }
        const { data } = await onlineBookApi.getPublic({
          data: payload,
        });
        if (isMounted) {
          setPublicBooks(data?.records || []);
        }
      } catch (error) {
        if (isMounted) {
          Notification.error('Thông báo', 'Lấy dữ liệu thất bại');
        }
      } finally {
        if (isMounted) {
          setLoadingPublic(false);
        }
      }
    };
    fetchPublic();
    return cleanup;
  }, [isLoggedIn, unitId]);

  const unitBooks = useMemo(() => {
    const storeBooks = values(onlineBooks || {});
    if (storeBooks.length) {
      return storeBooks;
    }
    return unitBooksLocal;
  }, [onlineBooks, unitBooksLocal]);

  const mergedBooks = useMemo(() => {
    if (!sharedBooks.length) {
      return unitBooks;
    }
    const merged = {
      ...keyBy(unitBooks, '_id'),
      ...keyBy(sharedBooks, '_id'),
    };
    return values(merged);
  }, [unitBooks, sharedBooks]);

  const baseBooks = useMemo(() => (isLoggedIn ? mergedBooks : publicBooks), [
    isLoggedIn,
    mergedBooks,
    publicBooks,
  ]);

  const filteredBooks = useMemo(() => {
    const keyword = searchText.trim().toLowerCase();
    const isValidBookLink = (link) => (
      isValidUrl(link) || link.startsWith('http://localhost') || link.startsWith('https://localhost')
    );
    const normalized = baseBooks.filter((book) => (
      book?.name
      && isValidBookLink(book?.bookLink || '')
    ));
    const byKeyword = keyword
      ? normalized.filter((book) => book.name.toLowerCase().includes(keyword))
      : normalized;
    if (!selectedTag) {
      return byKeyword;
    }
    return byKeyword.filter((book) => Array.isArray(book?.tags) && book.tags.includes(selectedTag));
  }, [searchText, baseBooks, selectedTag]);

  const availableTags = useMemo(() => {
    const tags = baseBooks.flatMap((book) => (Array.isArray(book?.tags) ? book.tags : []));
    return uniq(tags).filter(Boolean);
  }, [baseBooks]);

  const isLoading = handling || loadingShared || loadingPublic || loadingUnitBooks;

  const scrollToList = () => {
    const element = document.getElementById('online-books-list');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleStartReading = () => {
    setSearchText('');
    setSelectedTag('');
    scrollToList();
  };

  return (
    <Box sx={{ padding: { xs: 2, md: 4 } }}>
      {isLoading && <Waiting />}
      <Box
        sx={{
          borderRadius: 4,
          padding: { xs: 3, md: 6 },
          color: 'white',
          background: 'linear-gradient(135deg, #143a7b 0%, #1b76d2 55%, #16a5a8 100%)',
          position: 'relative',
          overflow: 'hidden',
          marginBottom: { xs: 3, md: 5 },
        }}
      >
        <Typography variant="h3" fontWeight={800}>
          Kho sách trực tuyến - không gian đọc hiện đại cho mọi lứa tuổi
        </Typography>
        <Typography
          variant="body1"
          sx={{
            opacity: 0.9,
            marginTop: 2,
            maxWidth: 520,
          }}
        >
          Khám phá bộ sưu tập được tuyển chọn với trải nghiệm mới: dễ tìm, dễ đọc và
          khơi gợi cảm hứng học tập mỗi ngày.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ marginTop: 3 }}>
          <Button
            variant="contained"
            sx={{ bgcolor: 'white', color: '#143a7b' }}
            onClick={scrollToList}
          >
            Khám phá ngay
          </Button>
        </Stack>
      </Box>

      <Box id="online-books-list" sx={{ marginBottom: { xs: 3, md: 6 } }}>
        <Typography variant="h5" fontWeight={700} sx={{ marginBottom: 2 }}>
          Danh sách sách trực tuyến
        </Typography>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          sx={{ marginBottom: 3 }}
        >
          <TextField
            fullWidth
            size="small"
            value={searchText}
            placeholder="Tìm theo tên sách..."
            onChange={(event) => setSearchText(event.target.value)}
          />
          <Chip
            label={`${filteredBooks.length} kết quả`}
            color="default"
            variant="outlined"
          />
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          sx={{ marginBottom: 3, flexWrap: 'wrap' }}
        >
          <Chip
            label="Tất cả"
            clickable
            color={!selectedTag ? 'primary' : 'default'}
            variant={!selectedTag ? 'filled' : 'outlined'}
            onClick={() => setSelectedTag('')}
            sx={{ marginBottom: 1 }}
          />
          {availableTags.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              clickable
              color={selectedTag === tag ? 'primary' : 'default'}
              variant={selectedTag === tag ? 'filled' : 'outlined'}
              onClick={() => setSelectedTag(tag)}
              sx={{ marginBottom: 1 }}
            />
          ))}
        </Stack>
        <Grid container spacing={3}>
          {filteredBooks.map((book) => {
            const bookTags = Array.isArray(book?.tags) ? book.tags : [];
            const tags = uniq(bookTags);
            const subtitle = book?.subtitle || '(Chưa có mô tả)';
            const coverLink = getCoverFallback(book);
            return (
              <Grid item xs={12} md={6} key={book._id || book.id}>
                <Card sx={{ display: 'flex', borderRadius: 3, height: '100%' }}>
                  <CardMedia
                    component="img"
                    image={getThumbnail(coverLink)}
                    alt={book.name}
                    sx={{ width: 180 }}
                    onError={(event) => {
                      const image = event.currentTarget;
                      if (image.src.includes('/defaultBook.jpg')) {
                        image.onerror = null;
                        return;
                      }
                      image.src = '/defaultBook.jpg';
                    }}
                  />
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" fontWeight={700}>
                      {book.name}
                    </Typography>

                    <Tooltip title={subtitle}>
                      <Box sx={{ height: '1.5em', marginTop: 1, overflow: 'hidden' }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            lineHeight: '1.5em',
                          }}
                        >
                          {subtitle}
                        </Typography>
                      </Box>
                    </Tooltip>

                    <Box sx={{ height: '24px', marginTop: 2, marginBottom: 1 }}>
                      {!!tags.length && (
                        <Stack direction="row" spacing={1} sx={{ overflow: 'hidden' }}>
                          {tags.map((tag) => (
                            <Chip key={tag} label={tag} size="small" />
                          ))}
                        </Stack>
                      )}
                    </Box>

                    <Stack direction="row" spacing={1.5} sx={{ marginTop: 'auto' }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => window.open(book.bookLink, '_blank')}
                      >
                        Xem ngay
                      </Button>
                      {/* <Button
                        variant="text"
                        size="small"
                        disabled={!book?.positionLink}
                        onClick={() => window.open(book.positionLink, '_blank')}
                      >
                        Vị trí sách
                      </Button> */}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
        {!filteredBooks.length && (
          <Box sx={{ marginTop: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Không tìm thấy sách phù hợp. Hãy thử từ khóa khác.
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          borderRadius: 3,
          padding: { xs: 3, md: 4 },
          backgroundColor: '#f1f6ff',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Sẵn sàng bắt đầu hành trình đọc mới?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ marginTop: 1 }}>
            Chọn bộ sưu tập phù hợp và mở sách trực tuyến ngay hôm nay.
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Button variant="contained" onClick={handleStartReading}>Bắt đầu đọc</Button>
        </Stack>
      </Box>
    </Box>
  );
}
